import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Camera, Upload, Trash2, RefreshCw, Plus, ChevronDown, ChevronUp,
  Check, Leaf, Pencil, AlertTriangle, Loader2, ShoppingCart, X, ArrowLeft, Refrigerator, ChefHat, ArrowRightLeft, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';


const API_URL = '';

function getToken() {
  return localStorage.getItem('gym_notebook_auth_token');
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_URL}/api/v2/fridge${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, ...opts.headers },
  });
  return res.json();
}

function FreshnessBar({ freshness, daysLeft }) {
  const color = freshness === 'green' ? 'bg-emerald-500' : freshness === 'yellow' ? 'bg-amber-400' : 'bg-red-500';
  const textColor = freshness === 'green' ? 'text-emerald-400' : freshness === 'yellow' ? 'text-amber-400' : 'text-red-400';
  const width = freshness === 'green' ? '100%' : freshness === 'yellow' ? '50%' : '15%';
  return (
    <div className="flex items-center gap-2 flex-1">
      <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width }} />
      </div>
      <span className={`text-xs font-medium ${textColor} whitespace-nowrap`}>
        {daysLeft < 0 ? 'Expired' : daysLeft === 0 ? 'Today' : `${daysLeft}d`}
      </span>
    </div>
  );
}

function FridgeItemRow({ item, onDelete, onRename }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState(item.name);
  const dotColor = item.freshness === 'green' ? 'bg-emerald-400' : item.freshness === 'yellow' ? 'bg-amber-400' : 'bg-red-400';
  const borderColor = item.freshness === 'green' ? 'border-emerald-500/30' : item.freshness === 'yellow' ? 'border-amber-500/30' : 'border-red-500/30';

  return (
    <div className={`bg-slate-800/60 rounded-2xl p-4 space-y-2 border ${borderColor}`}>
      {/* Row 1: Item info */}
      <div className="flex items-start gap-3">
        <div className={`w-3 h-3 rounded-full ${dotColor} shrink-0 mt-1.5`} />
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-1">
              <input value={newName} onChange={e => setNewName(e.target.value)}
                className="bg-slate-700 text-white text-sm rounded-lg px-3 py-1.5 flex-1 border-0 outline-none" autoFocus />
              <button onClick={() => { onRename(item.name, newName); setEditing(false); }}
                className="text-emerald-400 p-1.5"><Check className="h-4 w-4" /></button>
              <button onClick={() => setEditing(false)} className="text-slate-400 p-1.5"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <>
              <h4 className="text-white font-semibold text-base leading-tight break-words">{item.name}</h4>
              <p className="text-cyan-400 text-sm mt-0.5">{item.quantity}</p>
              {item.category && <p className="text-slate-500 text-xs mt-0.5">{item.category}</p>}
            </>
          )}
        </div>
      </div>
      {/* Row 2: Freshness bar + action buttons */}
      <div className="flex items-center gap-2 ml-6">
        <FreshnessBar freshness={item.freshness} daysLeft={item.days_left} />
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setEditing(true)} className="text-slate-500 hover:text-white p-1.5"><Pencil className="h-4 w-4" /></button>
          <button onClick={() => onDelete(item.name)} className="text-slate-500 hover:text-red-400 p-1.5"><X className="h-4 w-4" /></button>
          <button onClick={() => setExpanded(!expanded)} className="text-slate-400 p-1.5">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="ml-6 pt-1 text-sm text-slate-400 border-t border-slate-700/50">
          <p className="pt-2">Location: {item.location || 'Not specified'}</p>
        </div>
      )}
    </div>
  );
}

function RecipeCard({ recipe }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="bg-slate-800/60 border-slate-700 rounded-xl">
      <CardContent className="p-3">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div>
            <h4 className="text-white text-sm font-semibold">{recipe.name}</h4>
            <p className="text-xs text-slate-400">{recipe.cooking_time} - Saves {recipe.saves_items} item{recipe.saves_items > 1 ? 's' : ''}</p>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
        {expanded && (
          <div className="mt-3 space-y-2 text-xs text-slate-300">
            <div><span className="text-amber-400 font-medium">Uses:</span> {recipe.uses_expiring?.join(', ')}</div>
            <div><span className="text-cyan-400 font-medium">Ingredients:</span>
              <ul className="list-disc pl-4 mt-1">{recipe.ingredients?.map((ing, i) => <li key={i}>{ing}</li>)}</ul>
            </div>
            <div><span className="text-emerald-400 font-medium">Steps:</span>
              <ol className="list-decimal pl-4 mt-1">{recipe.steps?.map((step, i) => <li key={i}>{step}</li>)}</ol>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HealthScoreOrb({ items }) {
  const fresh = items.filter(i => i.freshness === 'green').length;
  const total = items.length || 1;
  const score = Math.round((fresh / total) * 100);
  const warn = items.filter(i => i.freshness === 'yellow').length;
  const crit = items.filter(i => i.freshness === 'red').length;
  const circumference = 2 * Math.PI * 40;
  const dashOffset = circumference - (score / 100) * circumference;
  const color = score >= 70 ? '#10b981' : score >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-24">
        <svg width="96" height="96" viewBox="0 0 96 96" className="transform -rotate-90">
          <circle cx="48" cy="48" r="40" fill="none" stroke="rgba(100,116,139,0.3)" strokeWidth="6" />
          <circle cx="48" cy="48" r="40" fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={circumference} strokeDashoffset={dashOffset} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-bold text-white">{score}%</span>
          <span className="text-[10px] text-slate-400">Health</span>
        </div>
      </div>
      <div className="space-y-1 text-sm">
        <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-emerald-400" /><span className="text-slate-300">{fresh} fresh</span></div>
        <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-amber-400" /><span className="text-slate-300">{warn} warning</span></div>
        <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-400" /><span className="text-slate-300">{crit} critical</span></div>
      </div>
    </div>
  );
}

export default function FridgeOracle({ onBack, onNavigate, isExpired }) {
  const [tab, setTab] = useState('fridge');
  const [items, setItems] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [grocery, setGrocery] = useState([]);
  const [newGroceryItem, setNewGroceryItem] = useState('');
  const [scanning, setScanning] = useState(false);
  const [refreshingRecipes, setRefreshingRecipes] = useState(false);
  const [suggestingShop, setSuggestingShop] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [swapSuggestion, setSwapSuggestion] = useState(null);
  const [swapLoading, setSwapLoading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const fileInputRef2 = useRef(null);

  const expiringCount = items.filter(i => i.freshness === 'yellow' || i.freshness === 'red').length;

  useEffect(() => { loadItems(); loadGrocery(); }, []);

  const loadItems = async () => {
    try { const data = await apiFetch('/items'); setItems(data.items || []); } catch (e) { /* */ }
  };

  const loadGrocery = async () => {
    try {
      const data = await apiFetch('/grocery');
      const raw = data.items || [];
      // Normalize: convert plain strings to {name, bought} objects
      setGrocery(raw.map(i => typeof i === 'string' ? { name: i, bought: false } : i));
    } catch (e) { /* */ }
  };

  const handleScan = async (file, mode = 'replace') => {
    setScanning(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await fetch(`${API_URL}/api/v2/fridge/scan?mode=${mode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData,
      });
      const { task_id } = await res.json();

      // Poll for result
      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 3000));
        const status = await apiFetch(`/scan-status/${task_id}`);
        if (status.status === 'done') {
          if (status.error) { toast.error(status.error); break; }
          toast.success(`Found ${status.items?.length || 0} items!`);
          break;
        }
        attempts++;
      }
    } catch (e) {
      console.error('Scan error:', e);
      toast.error('Scan processing... Results will appear shortly.');
    } finally {
      // Always reload items from DB — even if polling failed, the backend may have finished
      try { await loadItems(); } catch (_) {}
      setScanning(false);
    }
  };

  const handleMultiUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const firstMode = items.length ? 'append' : 'replace';
    await handleScan(files[0], firstMode);
    if (files[1]) {
      await handleScan(files[1], 'append');
    }
  };

  const handleUpload = (e) => { if (e.target.files?.[0]) handleScan(e.target.files[0], items.length ? 'append' : 'replace'); };
  const handleCamera = (e) => { if (e.target.files?.[0]) handleScan(e.target.files[0], items.length ? 'append' : 'replace'); };

  const deleteItem = async (name) => {
    await apiFetch(`/items/${encodeURIComponent(name)}`, { method: 'DELETE' });
    setItems(prev => prev.filter(i => i.name.toLowerCase() !== name.toLowerCase()));
  };

  const renameItem = async (oldName, newName) => {
    await apiFetch(`/items/${encodeURIComponent(oldName)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ new_name: newName }),
    });
    setItems(prev => prev.map(i => i.name.toLowerCase() === oldName.toLowerCase() ? { ...i, name: newName } : i));
  };

  const refreshRecipes = async () => {
    setRefreshingRecipes(true);
    try { const data = await apiFetch('/refresh-recipes', { method: 'POST' }); setRecipes(data.recipes || []); toast.success('Recipes updated!'); }
    catch (e) { toast.error('Failed to refresh recipes'); }
    finally { setRefreshingRecipes(false); }
  };

  const suggestShopping = async () => {
    setSuggestingShop(true);
    try {
      const data = await apiFetch('/suggest-shopping', { method: 'POST' });
      const newItems = (data.suggestions || []).map(s => ({
        name: typeof s === 'string' ? s : s.name,
        reason: typeof s === 'string' ? '' : (s.reason || ''),
        bought: false
      }));
      const updated = [...grocery, ...newItems];
      setGrocery(updated);
      apiFetch('/grocery', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: updated }) });
      toast.success(`Added ${newItems.length} suggestions!`);
    } catch (e) { toast.error('Failed to get suggestions'); }
    finally { setSuggestingShop(false); }
  };

  const toggleBought = (idx) => {
    setGrocery(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], bought: !updated[idx].bought };
      apiFetch('/grocery', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: updated }) });
      return updated;
    });
  };

  const addGroceryItem = () => {
    if (!newGroceryItem.trim()) return;
    const itemName = newGroceryItem.trim();
    const updated = [...grocery, { name: itemName, bought: false }];
    setGrocery(updated);
    setNewGroceryItem('');
    apiFetch('/grocery', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: updated }) });

    // Check for smart swap suggestion (non-blocking)
    const diabetesEnabled = localStorage.getItem('diabetes_enabled') === 'true';
    if (diabetesEnabled) {
      setSwapLoading(true);
      const unit = localStorage.getItem('diabetes_unit') || 'mmol/l';
      const API = process.env.REACT_APP_BACKEND_URL || '';
      fetch(`${API}/api/v2/grocery-swap/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ item_name: itemName, unit }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.swap && data.swap.should_swap) {
            setSwapSuggestion({ ...data.swap, originalItem: itemName });
          }
        })
        .catch(() => {})
        .finally(() => setSwapLoading(false));
    }
  };

  const applySwap = (swapName) => {
    // Replace the original item with the swap
    const updated = grocery.map(g =>
      g.name === swapSuggestion.originalItem ? { ...g, name: swapName } : g
    );
    setGrocery(updated);
    apiFetch('/grocery', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: updated }) });
    toast.success(`Swapped "${swapSuggestion.originalItem}" for "${swapName}"`);
    setSwapSuggestion(null);
  };

  const removeGroceryItem = (idx) => {
    const updated = grocery.filter((_, i) => i !== idx);
    setGrocery(updated);
    apiFetch('/grocery', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: updated }) });
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all fridge data and grocery list?')) return;
    setClearing(true);
    try { await apiFetch('/scans/all', { method: 'DELETE' }); setItems([]); setRecipes([]); setGrocery([]); toast.success('All data cleared'); }
    catch (e) { toast.error('Failed to clear data'); }
    finally { setClearing(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-testid="fridge-oracle">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-950 backdrop-blur-sm border-none ios-header" style={{ border: 'none' }}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-500 to-sky-600 flex items-center justify-center">
              <Refrigerator className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-white">FridgeOracle</h1>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={clearAll} disabled={clearing} className="text-red-400 hover:bg-red-500/10">
              {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            </Button>
            <button onClick={() => cameraInputRef.current?.click()} disabled={scanning}
              className="h-9 w-9 rounded-lg bg-cyan-600 hover:bg-cyan-500 flex items-center justify-center text-white disabled:opacity-50">
              {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </button>
            <button onClick={() => fileInputRef.current?.click()} disabled={scanning}
              className="h-9 w-9 rounded-lg border border-cyan-500/50 hover:bg-cyan-500/10 flex items-center justify-center text-cyan-400 disabled:opacity-50">
              <Upload className="h-4 w-4" />
            </button>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
        <input ref={fileInputRef2} type="file" accept="image/*" multiple className="hidden" onChange={handleMultiUpload} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleCamera} />
      </header>

      <main className="container mx-auto px-4 py-4 pb-32 max-w-lg">
        {/* Alert Banner */}
        {expiringCount > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-center gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
            <span className="text-amber-400 text-sm">{expiringCount} item{expiringCount > 1 ? 's' : ''} expiring soon!</span>
          </div>
        )}

        {/* Health Score */}
        {items.length > 0 && (
          <Card className="bg-slate-800/50 border-slate-700 rounded-xl mb-4">
            <CardContent className="p-4 flex justify-center">
              <HealthScoreOrb items={items} />
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-800/50 rounded-xl p-1 mb-4">
          {[
            { id: 'fridge', label: 'Fridge', Icon: Refrigerator },
            { id: 'recipes', label: 'Recipes', Icon: ChefHat },
            { id: 'grocery', label: 'Grocery', Icon: ShoppingCart },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                tab === t.id ? 'bg-cyan-600 text-white shadow-lg' : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
              }`}
              data-testid={`fridge-tab-${t.id}`}>
              <t.Icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Fridge Tab */}
        {tab === 'fridge' && (
          <div className="space-y-2">
            {items.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Camera className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p className="text-sm mb-4">Take a photo of your fridge to get started</p>
                <div className="flex gap-2 justify-center">
                  <button onClick={() => cameraInputRef.current?.click()} disabled={scanning}
                    className="py-2.5 px-4 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium flex items-center gap-2">
                    <Camera className="h-4 w-4" /> Take Photo
                  </button>
                  <button onClick={() => fileInputRef2.current?.click()} disabled={scanning}
                    className="py-2.5 px-4 rounded-xl border border-amber-500/40 text-amber-400 text-sm font-medium hover:bg-amber-500/10 flex items-center gap-2"
                    data-testid="upload-2-photos-empty-btn">
                    <Upload className="h-4 w-4" /> Upload 2 Photos
                  </button>
                </div>
              </div>
            ) : (
              items.sort((a, b) => a.days_left - b.days_left).map((item, i) => (
                <FridgeItemRow key={`${item.name}-${i}`} item={item} onDelete={deleteItem} onRename={renameItem} />
              ))
            )}
            {items.length > 0 && (
              <div className="flex gap-2 pt-2">
                <button onClick={() => cameraInputRef.current?.click()} disabled={scanning}
                  className="flex-1 py-3 rounded-xl border border-cyan-500/30 text-cyan-400 text-sm font-medium hover:bg-cyan-500/10 flex items-center justify-center gap-2">
                  <Camera className="h-4 w-4" /> Photo
                </button>
                <button onClick={() => fileInputRef.current?.click()} disabled={scanning}
                  className="flex-1 py-3 rounded-xl border border-slate-600 text-slate-400 text-sm font-medium hover:bg-slate-700/50 flex items-center justify-center gap-2">
                  <Upload className="h-4 w-4" /> Upload
                </button>
                <button onClick={() => fileInputRef2.current?.click()} disabled={scanning}
                  className="flex-1 py-3 rounded-xl border border-amber-500/30 text-amber-400 text-sm font-medium hover:bg-amber-500/10 flex items-center justify-center gap-2"
                  data-testid="upload-2-photos-btn">
                  <Upload className="h-4 w-4" /> 2 Photos
                </button>
              </div>
            )}
          </div>
        )}

        {/* Recipes Tab */}
        {tab === 'recipes' && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button onClick={refreshRecipes} disabled={refreshingRecipes || items.length === 0} size="sm" variant="outline"
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
                {refreshingRecipes ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Refresh
              </Button>
            </div>
            {recipes.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <p className="text-sm">Scan your fridge to get rescue recipe suggestions</p>
              </div>
            ) : (
              recipes.map((recipe, i) => <RecipeCard key={i} recipe={recipe} />)
            )}
          </div>
        )}

        {/* Grocery Tab */}
        {tab === 'grocery' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input value={newGroceryItem} onChange={e => setNewGroceryItem(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addGroceryItem()}
                placeholder="Add item..." className="flex-1 bg-slate-800 text-white rounded-xl px-3 py-2 text-sm border border-slate-700 outline-none focus:border-teal-500" />
              <button onClick={addGroceryItem} className="h-10 w-10 rounded-xl bg-teal-600 hover:bg-teal-500 flex items-center justify-center text-white">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button onClick={suggestShopping} disabled={suggestingShop} size="sm" variant="outline"
              className="w-full border-teal-500/50 text-teal-400 hover:bg-teal-500/10">
              {suggestingShop ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShoppingCart className="h-4 w-4 mr-1" />}
              AI Suggest Shopping
            </Button>

            {/* Smart Swap Suggestion */}
            {swapLoading && (
              <div className="flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-xl px-3 py-2">
                <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
                <span className="text-xs text-purple-300">Checking for healthier alternatives...</span>
              </div>
            )}
            {swapSuggestion && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 space-y-2" data-testid="swap-suggestion">
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4 text-amber-400" />
                  <span className="text-xs font-medium text-amber-300">Smart Swap Suggestion</span>
                  <button onClick={() => setSwapSuggestion(null)} className="ml-auto text-slate-500 hover:text-slate-300">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <p className="text-xs text-slate-300">{swapSuggestion.reason}</p>
                {swapSuggestion.suggestions?.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => applySwap(s.name)}
                    className="w-full flex items-center gap-2 bg-slate-800/60 hover:bg-slate-700/60 rounded-lg px-3 py-2 transition-colors text-left"
                    data-testid={`swap-option-${i}`}
                  >
                    <ArrowRightLeft className="h-3 w-3 text-amber-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-white font-medium">{s.name}</span>
                      <p className="text-[10px] text-slate-400">{s.benefit} {s.gi && `(GI: ${s.gi})`}</p>
                    </div>
                  </button>
                ))}
                {swapSuggestion.tip && (
                  <div className="flex items-start gap-1.5 mt-1">
                    <Lightbulb className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" />
                    <span className="text-[10px] text-slate-400">{swapSuggestion.tip}</span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1">
              {grocery.map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-slate-800/60 rounded-xl px-3 py-2">
                  <button onClick={() => toggleBought(i)}
                    className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${item.bought ? 'bg-teal-600' : 'border border-slate-600'}`}>
                    {item.bought && <Check className="h-3.5 w-3.5 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm ${item.bought ? 'text-slate-500 line-through' : 'text-white'}`}>{item.name}</span>
                    {item.reason && (
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{item.reason}</p>
                    )}
                  </div>
                  <button onClick={() => removeGroceryItem(i)} className="text-slate-500 hover:text-red-400 p-1 shrink-0">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            {items.length > 0 && (
              <div className="flex gap-3 pt-2">
                <div className="flex items-center gap-1.5 bg-emerald-500/10 rounded-lg px-3 py-2">
                  <Leaf className="h-4 w-4 text-emerald-400" />
                  <span className="text-xs text-emerald-400">{(items.filter(i => i.freshness !== 'red').length * 0.3).toFixed(1)}kg CO2</span>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
